/**
 * DatasetPreFlight.js — Dataset Quality Analysis
 * Runs automatically when a dataset is loaded
 * Shows data quality issues, column detection, label mapping preview
 * before the main report sections
 */
import React, { useMemo } from "react";
import { severityFromLabel, getMitre, SEVERITY_COLORS } from "../reportUtils";

// ── Dataset Type Classifier ───────────────────────────────────────────────────

function classifyDataset(columns, theme) {
  const cols = columns.map(c => c.toLowerCase());
  const has  = (keywords) => keywords.some(k => cols.some(c => c.includes(k)));

  if (has(["modbus","coil","register","dnp3","profinet","scada","plc"]))
    return { type: "ICS/OT Dataset",        icon: "🏭", color: "#E65100",
             note: "Industrial Control System traffic — Modbus/DNP3 protocol data" };
  if (has(["flow bytes","flow packet","fwd packet","bwd packet","flow iat"]))
    return { type: "Flow Feature Dataset",  icon: "📊", color: theme.colors.accent,
             note: "CICFlowMeter-generated features (CIC-IDS2017/2018 style) — no raw IPs or timestamps" };
  if (has(["src_ip","source ip","sourceip","dst_ip","dest ip","destip","id.orig_h","id.resp_h"]))
    return { type: "Network Flow Log",      icon: "🌐", color: "#2E7D32",
             note: "Network flow log with IP addresses — full network attribution available" };
  if (has(["eventid","event_id","winlog","channel","provider"]))
    return { type: "Windows Event Log",     icon: "🪟", color: theme.colors.accent,
             note: "Windows Security Event Log — host-based detection data" };
  if (has(["uid","id.orig_h","id.resp_h","proto","service","conn_state"]))
    return { type: "Zeek/Bro Log",          icon: "🔍", color: "#00695C",
             note: "Zeek network security monitor log — full connection data" };
  if (has(["alert","signature","category","priority","event_type"]))
    return { type: "IDS/SIEM Alert Log",    icon: "🚨", color: "#C62828",
             note: "IDS/SIEM alert export — signature-based detection data" };
  if (has(["timestamp","src","dst","proto","bytes"]))
    return { type: "Generic Network Log",   icon: "📋", color: theme.colors.textMuted,
             note: "Generic network log format" };
  return { type: "Unknown Format",          icon: "❓", color: theme.colors.textMuted,
           note: "Column structure not recognized — analysis may be limited" };
}

// ── Column Confidence Scorer ──────────────────────────────────────────────────

function detectColumns(columns) {
  const cols = columns.map(c => c.toLowerCase().trim().replace(/[\s_\-\.]+/g, ""));

  const findCol = (candidates, label) => {
    for (const c of candidates) {
      const norm = c.toLowerCase().replace(/[\s_\-\.]+/g, "");
      const idx  = cols.indexOf(norm);
      if (idx !== -1) return { found: columns[idx], confidence: "HIGH",   label };
    }
    // Partial match
    for (const c of candidates) {
      const norm = c.toLowerCase().replace(/[\s_\-\.]+/g, "");
      const idx  = cols.findIndex(col => col.includes(norm) || norm.includes(col));
      if (idx !== -1) return { found: columns[idx], confidence: "MEDIUM", label };
    }
    return { found: null, confidence: "NONE", label };
  };

  return {
    label:    findCol(["label","type","attack_type","class","category","signature","Label","Attack Type","attack cat"], "Attack Label"),
    srcIP:    findCol(["srcip","src_ip","sourceip","source_ip","id.orig_h","initiatorip","Source IP","Src IP"], "Source IP"),
    dstIP:    findCol(["dstip","dst_ip","destip","dest_ip","id.resp_h","Destination IP","Dst IP"], "Destination IP"),
    srcPort:  findCol(["sport","src_port","sourceport","id.orig_p","Source Port"], "Source Port"),
    dstPort:  findCol(["dport","dst_port","destport","id.resp_p","Destination Port","destinationport"], "Destination Port"),
    protocol: findCol(["proto","protocol","transport","Protocol"], "Protocol"),
    timestamp:findCol(["timestamp","time","date","datetime","Timestamp","ts","Flow Timestamp"], "Timestamp"),
    bytes:    findCol(["bytes","length","size","sbytes","Total Length","Payload"], "Bytes/Payload"),
  };
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function DatasetPreFlight({ data, columns, fileName, theme, SEVERITY_COLORS: SC }) {
  if (!data || !data.length || !columns.length) return null;

  const analysis = useMemo(() => {
    // Dataset type
    const datasetType  = classifyDataset(columns, theme);
    const colDetection = detectColumns(columns);

    // Label column
    const labelCol = colDetection.label.found;
    const labels   = {};
    let nullCount  = 0;
    let infCount   = 0;
    let dupCount   = 0;
    const seen     = new Set();
    const encodingIssues = new Set();

    // ── Chunked analysis ─────────────────────────────────────────────────────
    // Process CHUNK_SIZE rows at a time, aggregate results, discard chunk.
    // This keeps memory flat regardless of dataset size.
    const CHUNK_SIZE = 2000;
    const totalRows  = data.length;

    for (let chunkStart = 0; chunkStart < totalRows; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, totalRows);

      for (let i = chunkStart; i < chunkEnd; i++) {
        const row = data[i];

        // Label distribution + encoding (every row)
        if (labelCol) {
          const label = String(row[labelCol] || "").trim();
          labels[label] = (labels[label] || 0) + 1;
          if ([...label].some(c => c.charCodeAt(0) > 127)) {
            encodingIssues.add(label);
          }
        }

        // Null check (every row — just checks values)
        const vals = Object.values(row);
        if (vals.some(v => v === null || v === undefined || v === "")) nullCount++;

        // Infinity check (every row)
        if (vals.some(v => v === Infinity || v === -Infinity || String(v) === "Infinity")) infCount++;

        // Duplicate check — lightweight key from label + first 2 numeric cols
        // Avoids JSON.stringify — O(1) per row instead of O(columns)
        const dupKey = (labelCol ? String(row[labelCol]||"") : "") + "|" +
                       vals.slice(0, 2).join("|");
        if (seen.has(dupKey)) dupCount++;
        else seen.add(dupKey);
      }
      // chunk processed — GC can reclaim references from this iteration
    }

    // Clean label map (strip encoding artifacts for display)
    const cleanLabel = (l) => l.replace(/[^\x00-\x7F]/g, "").replace(/\s+/g, " ").trim();

    // Label analysis with severity + MITRE preview
    const labelAnalysis = Object.entries(labels)
      .sort((a,b) => b[1]-a[1])
      .map(([raw, count]) => {
        const clean   = cleanLabel(raw);
        const sev     = severityFromLabel(raw);
        const mitre   = getMitre(raw);
        const hasArtifact = encodingIssues.has(raw);
        return { raw, clean, count, pct: ((count/data.length)*100).toFixed(1), sev, mitre, hasArtifact };
      });

    // Blocked analyses
    const blocked = [];
    if (!colDetection.srcIP.found)    blocked.push({ item:"Source IP Attribution",  reason:"No source IP column detected",          fix:"Use a dataset with src_ip or Source IP column" });
    if (!colDetection.dstIP.found)    blocked.push({ item:"Target Identification",   reason:"No destination IP column detected",     fix:"Use a dataset with dst_ip or Destination IP column" });
    if (!colDetection.timestamp.found)blocked.push({ item:"Attack Timeline & Heatmap",reason:"No timestamp column detected",         fix:"Datasets with Timestamp column enable timeline analysis" });
    if (!colDetection.protocol.found) blocked.push({ item:"Protocol Analysis",       reason:"No protocol column detected",           fix:"Include proto or Protocol column" });

    // Quality score
    let score = 100;
    if (dupCount > data.length * 0.10) score -= 15;
    if (infCount > 100)                score -= 10;
    if (nullCount > data.length * 0.05)score -= 10;
    if (!colDetection.label.found)     score -= 30;
    if (!colDetection.srcIP.found)     score -= 15;
    if (!colDetection.timestamp.found) score -= 10;
    if (encodingIssues.size > 0)       score -= 5;
    score = Math.max(score, 0);

    // Recommendations
    const recommendations = [];
    if (dupCount > 0) recommendations.push({
      severity: dupCount > data.length*0.10 ? "high" : "medium",
      msg: `${dupCount.toLocaleString()} duplicate rows detected (${((dupCount/data.length)*100).toFixed(1)}% of dataset)`,
      action: "Consider removing duplicates before analysis — they inflate event counts and skew severity metrics"
    });
    if (infCount > 0) recommendations.push({
      severity: "medium",
      msg: `${infCount.toLocaleString()} rows contain Infinity values (likely in Flow Bytes/s or Packets/s)`,
      action: "NexGen auto-replaces Infinity with 0 — no action needed but note these rows in your methodology"
    });
    if (nullCount > 0) recommendations.push({
      severity: "low",
      msg: `${nullCount.toLocaleString()} rows have null/empty values`,
      action: "NexGen fills null numerics with 0 — non-numeric nulls treated as empty string"
    });
    if (encodingIssues.size > 0) recommendations.push({
      severity: "medium",
      msg: `${encodingIssues.size} label(s) contain encoding artifacts (ï¿½ characters from UTF-8 encoding issues)`,
      action: "NexGen auto-cleans these for MITRE/severity mapping — display labels may look unusual"
    });
    if (datasetType.type === "Flow Feature Dataset") recommendations.push({
      severity: "info",
      msg: "This is a CICFlowMeter flow feature dataset — no raw IP addresses or wall-clock timestamps are present",
      action: "Network attribution and timeline heatmap sections will be limited or unavailable"
    });

    return { datasetType, colDetection, labelAnalysis, blocked, score, recommendations,
             dupCount, infCount, nullCount, encodingIssues: [...encodingIssues] };
  }, [data, columns, theme]);

  const { datasetType, colDetection, labelAnalysis, blocked, score, recommendations } = analysis;

  const scoreColor = score >= 80 ? SC.low : score >= 60 ? SC.medium : score >= 40 ? SC.high : SC.critical;
  const scoreLabel = score >= 80 ? "GOOD" : score >= 60 ? "FAIR" : score >= 40 ? "POOR" : "CRITICAL ISSUES";

  return (
    <div style={{ marginBottom: 24, border: `1px solid ${datasetType.color}44`, borderRadius: 12, overflow: "hidden" }}>

      {/* Header */}
      <div style={{ background: `${datasetType.color}15`, borderBottom: `1px solid ${datasetType.color}33`, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color: datasetType.color, letterSpacing: 2 }}>
            {datasetType.icon} DATASET PRE-FLIGHT ANALYSIS
          </div>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
            {fileName} · {data.length.toLocaleString()} rows · {columns.length} columns
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1 }}>QUALITY SCORE</div>
          <div style={{ fontFamily: "monospace", fontSize: 24, fontWeight: 900, color: scoreColor }}>{score}/100</div>
          <div style={{ fontFamily: "monospace", fontSize: 10, color: scoreColor, fontWeight: 700 }}>{scoreLabel}</div>
        </div>
      </div>

      <div style={{ padding: 20, background: theme.colors.card }}>

        {/* Row 1: Dataset Type + Column Detection */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>

          {/* Dataset Type */}
          <div style={{ background: `${datasetType.color}08`, border: `1px solid ${datasetType.color}33`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>DATASET TYPE</div>
            <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 900, color: datasetType.color, marginBottom: 4 }}>
              {datasetType.icon} {datasetType.type}
            </div>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>{datasetType.note}</div>
          </div>

          {/* Column Detection */}
          <div style={{ background: theme.colors.cardBorder + "11", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>COLUMN DETECTION</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {Object.values(colDetection).map(({ label, found, confidence }) => {
                const color = confidence === "HIGH" ? SC.low : confidence === "MEDIUM" ? SC.medium : SC.critical;
                return (
                  <div key={label} style={{ padding: "3px 8px", background: `${color}12`, border: `1px solid ${color}33`, borderRadius: 4 }}>
                    <span style={{ fontFamily: "monospace", fontSize: 10, color }}>
                      {confidence === "NONE" ? "✕" : confidence === "MEDIUM" ? "~" : "✓"} {label}
                    </span>
                    {found && <span style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted, marginLeft: 4 }}>({found})</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 2: Label Analysis Table */}
        <div style={{ marginBottom: 16, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ background: theme.colors.tableHeader, padding: "10px 14px", fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: theme.colors.textPrimary, letterSpacing: 1 }}>
            🏷️ LABEL ANALYSIS — SEVERITY & MITRE PREVIEW
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
            <thead>
              <tr style={{ background: theme.colors.rowAlt }}>
                {["Raw Label","Clean Label","Events","%","NexGen Severity","MITRE Tactic","Technique","Issues"].map(h => (
                  <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `1px solid ${theme.colors.cardBorder}`, fontSize: 10, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labelAnalysis.map((l, i) => {
                const sevColor = SC[l.sev] || SC.info;
                return (
                  <tr key={i} style={{ background: i%2===0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                    <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontFamily: "monospace", fontSize: 10 }}>{l.raw}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>{l.clean}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textPrimary }}>{l.count.toLocaleString()}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>{l.pct}%</td>
                    <td style={{ padding: "7px 10px" }}>
                      <span style={{ padding: "2px 6px", background: `${sevColor}18`, border: `1px solid ${sevColor}44`, borderRadius: 4, color: sevColor, fontSize: 10, fontWeight: 700 }}>
                        {l.sev.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: "7px 10px", color: theme.colors.accent, fontSize: 10 }}>{l.mitre.tactic}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.accent, fontSize: 10 }}>{l.mitre.technique}</td>
                    <td style={{ padding: "7px 10px" }}>
                      {l.hasArtifact && <span style={{ padding: "2px 5px", background: `${SC.medium}18`, border: `1px solid ${SC.medium}33`, borderRadius: 3, color: SC.medium, fontSize: 9, fontWeight: 700 }}>⚠ ENCODING</span>}
                      {l.mitre.tactic === "Unknown" && <span style={{ padding: "2px 5px", background: `${SC.high}18`, border: `1px solid ${SC.high}33`, borderRadius: 3, color: SC.high, fontSize: 9, fontWeight: 700, marginLeft: 4 }}>⚠ UNMAPPED</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Row 3: Recommendations + Blocked */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

          {/* Recommendations */}
          <div>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>⚠ DATA QUALITY FINDINGS</div>
            {recommendations.length === 0 ? (
              <div style={{ padding: 12, background: `${SC.low}08`, border: `1px solid ${SC.low}33`, borderRadius: 6, fontFamily: "monospace", fontSize: 11, color: SC.low }}>
                ✓ No data quality issues detected
              </div>
            ) : recommendations.map((r, i) => {
              const color = SC[r.severity] || SC.info;
              return (
                <div key={i} style={{ marginBottom: 8, padding: "10px 12px", background: `${color}08`, border: `1px solid ${color}33`, borderRadius: 6 }}>
                  <div style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: "#FFD54F", marginBottom: 4 }}>{r.msg}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textLabel }}>→ {r.action}</div>
                </div>
              );
            })}
          </div>

          {/* Blocked analyses */}
          <div>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>🚫 ANALYSIS LIMITATIONS</div>
            {blocked.length === 0 ? (
              <div style={{ padding: 12, background: `${SC.low}08`, border: `1px solid ${SC.low}33`, borderRadius: 6, fontFamily: "monospace", fontSize: 11, color: SC.low }}>
                ✓ All analysis modules available
              </div>
            ) : blocked.map((b, i) => (
                <div style={{ marginBottom: 8, padding: "10px 12px", background: "rgba(150,150,150,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }}>
                <div style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: "#EF9A9A", marginBottom: 2 }}>✕ {b.item}</div>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textSecondary }}>{b.reason}</div>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#81C784", marginTop: 2 }}>Fix: {b.fix}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
